/**
 * Centralizes page_context extraction and normalization for API payloads.
 * Keep create and send-message flows aligned while preserving each flow's
 * expected value-shape rules.
 */
export class PageContextNormalizer {
  /**
   * Accepts both page_context (server shape) and pageContext (framework shape).
   */
  static extractPageContextCandidate(
    data?: Record<string, unknown>,
  ): Record<string, unknown> | undefined {
    const candidate =
      (data?.page_context as Record<string, unknown> | undefined) ??
      (data?.pageContext as Record<string, unknown> | undefined);

    if (
      !candidate ||
      typeof candidate !== 'object' ||
      Array.isArray(candidate) ||
      candidate === null
    ) {
      return undefined;
    }

    return candidate;
  }

  static normalizePageContextForCreate(
    candidate: Record<string, unknown>,
  ): Record<string, unknown> {
    // Create should preserve explicit {value: ...} objects.
    return this.normalizeObject(candidate, false);
  }

  static normalizePageContextForMessage(
    candidate: Record<string, unknown>,
  ): Record<string, unknown> {
    // Send-message should unwrap framework value wrappers.
    return this.normalizeObject(candidate, true);
  }

  private static normalizeObject(
    obj: Record<string, unknown>,
    unwrapValueObjects: boolean,
  ): Record<string, unknown> {
    return Object.fromEntries(
      Object.entries(obj).flatMap(([key, value]) => {
        const normalized = this.normalizeValue(value, unwrapValueObjects);
        return normalized === undefined ? [] : [[key, normalized]];
      }),
    );
  }

  private static normalizeValue(
    value: unknown,
    unwrapValueObjects: boolean,
  ): unknown {
    // Optional unwrapping keeps compatibility with framework-provided
    // {value: ...} wrappers while preserving normal object structures.
    const shouldUnwrapValueObject =
      unwrapValueObjects &&
      value &&
      typeof value === 'object' &&
      !Array.isArray(value) &&
      Object.keys(value as Record<string, unknown>).length === 1 &&
      'value' in (value as Record<string, unknown>);
    const normalizedInput = shouldUnwrapValueObject
      ? (value as {value: unknown}).value
      : value;

    if (normalizedInput === undefined) {
      return undefined;
    }

    if (typeof normalizedInput === 'bigint') {
      return String(normalizedInput);
    }

    if (Array.isArray(normalizedInput)) {
      return normalizedInput.map((item) =>
        this.normalizeValue(item, unwrapValueObjects),
      );
    }

    if (!normalizedInput || typeof normalizedInput !== 'object') {
      return normalizedInput;
    }

    return this.normalizeObject(
      normalizedInput as Record<string, unknown>,
      unwrapValueObjects,
    );
  }
}
